/**
 * Affiliate & Referral Agreements — 6 documents.
 *
 * Affiliate programs look like free growth: you only pay for sales that happen. The cost shows up
 * elsewhere — in coupon stacking on a low-basket product, in paid-search traffic you already owned,
 * in marketplace listings you never authorised, and in claims made about your product by people you
 * are paying, which the brand carries whether or not it wrote them.
 */
export const AFFILIATE_DOCS = [
  {
    id: "affiliate-agreement",
    name: "Affiliate Agreement",
    category: "affiliate",
    subcategory: "Core affiliate",
    aliases: [
      "Affiliate Program Agreement",
      "Affiliate Terms and Conditions",
      "Publisher Agreement",
      "Performance Marketing Agreement",
    ],
    stages: ["launch", "growth"],
    importance: "common",
    entityTypes: ["any"],
    parties: "Brand + Affiliate (Publisher)",
    whenYouNeedIt:
      "Before you open an affiliate program, install a tracking platform, or hand out your first commission code. Also before you join a network, because the network's terms and your own program terms have to agree with each other.",
    whatItDoes:
      "Sets the rules of your affiliate program: how a sale is attributed, what commission is paid, when it is paid, what happens when the order is refunded, how affiliates may and may not promote you, and how you remove someone from the program.",
    whyItMatters:
      "Affiliate programs are sold as risk-free because you pay on results, and for a low-basket consumable that framing is misleading. Stack an affiliate commission on top of a coupon code, a first-order discount, free shipping and the parcel cost, and a single-unit order can cost you more than it earns before the customer has tried the product once. The second exposure is what affiliates say to make the sale. If someone earning your commission tells an audience your drink treats a condition or your supplement causes weight loss, the claim attaches to your brand, and what may be said about a food, beverage or supplement is a question for counsel rather than for a publisher chasing conversion.",
    majorTerms: [
      "Commission rate, structure and any category or SKU exclusions",
      "Attribution model, tracking method and cookie or session window",
      "Coupon code issuance, uniqueness and stacking rules",
      "Order value the commission is calculated on: gross, net of discount, net of shipping",
      "Payment timing, minimum payout threshold and holding period",
      "Refund, cancellation and chargeback clawback mechanics",
      "Subscription and repeat-purchase commission treatment",
      "Prohibited promotional methods and channels",
      "Trademark and brand asset usage rules",
      "Claims and messaging restrictions, with a pre-approved claims list",
      "Disclosure obligations for paid promotion",
      "Marketplace and reseller prohibition",
      "Termination, code deactivation and final payment",
    ],
    clausesToWatch: [
      {
        clause: "Attribution and the tracking window",
        why: "A long last-click window pays commission on customers who were already coming to you from email, search or a podcast, converting existing demand into a permanent cost.",
      },
      {
        clause: "Coupon code stacking",
        why: "If a commission code can be combined with a site-wide promotion and a first-order discount, the same order can be discounted twice and commissioned once, which on a low-priced consumable can take the order below cost.",
      },
      {
        clause: "Brand-term bidding in paid search",
        why: "An affiliate bidding on your own brand name intercepts traffic you already owned, charges you commission on it, and raises the price you pay for your own keywords at the same time.",
      },
      {
        clause: "Refund and chargeback clawback",
        why: "Without a clawback and a holding period, you pay commission on orders that are refunded, disputed or never collected, and on a consumable with a return-friendly guarantee that adds up.",
      },
      {
        clause: "Claims and messaging restrictions",
        why: "Statements an affiliate makes to sell your product attach to your brand, so the agreement needs an approved claims list and a prohibition on going beyond it.",
      },
      {
        clause: "Marketplace and resale prohibition",
        why: "An affiliate that buys at a discount and lists on a marketplace becomes an unauthorised seller competing with your own listing and undercutting your retail partners.",
      },
      {
        clause: "Trademark and asset usage",
        why: "Without a defined asset kit and usage rules, affiliates build lookalike sites, use old packaging images and register domains containing your brand name.",
      },
      {
        clause: "Termination and code deactivation",
        why: "If codes stay live and content stays published after removal, you keep paying for traffic from someone you have already terminated.",
      },
    ],
    cpgIssues: [
      "Average order value in consumables is low and parcel cost is high, so the fully loaded cost of an affiliate order — commission, discount, shipping, packaging, payment fees — has to be modelled per order rather than assumed to fit inside gross margin.",
      "Coupon-code stacking is the specific margin killer: a single-unit beverage or supplement order carrying an affiliate commission and a stacked promotional code can end up contributing nothing.",
      "Health, structure-function and weight-loss style claims about a supplement or functional beverage are exactly what affiliates reach for to convert, and what may be said about a product of this kind is jurisdiction-specific and fact-specific — the brand carries the exposure for claims made by people it pays, so this belongs with counsel and in a written approved-claims list.",
      "Affiliates listing your product on marketplaces creates unauthorised sellers, breaks your pricing structure and can disrupt an authorised marketplace strategy you have already built.",
      "Subscription products need an explicit rule: commission on the first order only, on a fixed number of cycles, or ongoing, because paying forever on a recurring order changes the lifetime economics completely.",
      "Coupon and deal sites will capture checkout intent from customers already on your site, which is a different transaction from acquiring a new customer and often deserves a different rate or exclusion.",
      "Affiliates promoting to audiences outside your shipping footprint generate orders you cannot fulfil at a sensible cost, so territory and shipping limits belong in the program terms.",
      "Any promotion suggesting your product is suitable for children, pregnancy or a medical condition should be prohibited outright and escalated to counsel rather than handled by a program manager.",
    ],
    founderMistakes: [
      "Launching a program with a commission rate copied from an apparel brand, on a product with a fraction of the basket size and a much higher shipping cost per order.",
      "Allowing codes to stack with site-wide promotions, and running a launch month where the best-performing affiliate orders lost money on every unit.",
      "Not prohibiting brand-term bidding, and paying commission on customers who searched for the brand by name.",
      "Paying commission weekly with no holding period, then absorbing refunds on a money-back guarantee with no way to claw the commission back.",
      "Finding a top affiliate had listed the product on a marketplace at a discount, competing with the brand's own listing.",
      "Discovering an affiliate blog describing the product as a treatment for a health condition, and having no contractual basis to require removal quickly.",
      "Terminating an affiliate and leaving the code active, continuing to pay on it for months.",
    ],
    negotiationPoints: [
      "Model the fully loaded cost of a representative order before setting the rate, and set the commission base net of discounts and shipping.",
      "Set a short attribution window, and consider a lower rate or exclusion for coupon and deal traffic that converts existing demand.",
      "Prohibit stacking commission codes with other active promotions, and issue unique codes per affiliate so attribution and abuse are both traceable.",
      "Prohibit bidding on your brand name and close variants, and require paid-search activity to be disclosed in advance.",
      "Hold commission for a period matched to your refund window, with automatic clawback on refunds and chargebacks.",
      "Attach an approved claims list and an asset kit, and prohibit any statement outside it — with counsel setting the list for supplements and functional products.",
      "Prohibit marketplace listings and any resale of product bought at a discount, with immediate termination as the remedy.",
      "Require disclosure of the paid relationship in every promotion, in the form counsel advises for the channels you use.",
      "Reserve the right to terminate immediately, deactivate codes and require content removal.",
    ],
    redFlags: [
      "No clawback on refunded, cancelled or charged-back orders.",
      "Commission calculated on gross order value before discounts and shipping.",
      "No restriction on bidding against your own brand terms.",
      "Codes that stack with any other promotion by default.",
      "No claims restrictions and no approved-claims list for a supplement or functional product.",
      "Silence on marketplace listings and resale.",
      "No disclosure requirement for paid promotion.",
      "Program terms the affiliate network can amend without notice to you.",
    ],
    beforeYouSign: [
      "A fully loaded unit economics model for a single-unit order carrying commission, discount, shipping and payment fees.",
      "A written approved-claims list, reviewed by counsel, for every product in the program.",
      "Your tracking setup tested end to end, including refunds flowing back through it.",
      "The network's terms, if you are joining one, checked against your own program terms for conflicts.",
    ],
    related: [
      "influencer-affiliate-agreement",
      "brand-ambassador-agreement",
      "commission-referral-agreement",
      "affiliate-disclosure",
      "refund-policy",
      "terms-and-conditions",
    ],
    attorneyReview: "strongly-recommended",
    attorneyNote:
      "Claims made by people you pay to promote a supplement or functional product attach to your brand, and disclosure obligations for paid promotion are consumer-facing and vary by jurisdiction and channel.",
    templateId: "affiliate-agreement",
    templateVersion: "1.0",
    lastReviewed: "2026-08-25",
  },

  {
    id: "referral-agreement",
    name: "Referral Agreement",
    category: "affiliate",
    subcategory: "Referral",
    aliases: [
      "Introduction Agreement",
      "Finder's Agreement",
      "Referral Fee Agreement",
      "Business Referral Agreement",
    ],
    stages: ["launch", "distribution", "growth"],
    importance: "situational",
    entityTypes: ["any"],
    parties: "Brand + Referring Party",
    whenYouNeedIt:
      "When someone offers to introduce you to a buyer, a distributor, a co-packer or an account and expects to be paid if it works. Paper it before the introduction, because after the meeting the price of the introduction is set by how well it went.",
    whatItDoes:
      "Defines what counts as a referral, what has to happen before a fee is owed, how much it is and when it is paid, and makes clear that the referrer speaks for themselves rather than for your brand.",
    whyItMatters:
      "Referral arrangements go wrong over definition, not over money. Someone forwards an email to a buyer you were already talking to and later claims a fee on a chain launch worth six figures. A referral agreement that says what a qualified referral is, requires it to be registered in advance, and states when the fee is earned turns an argument into an arithmetic problem. It also matters that a referrer is not authorised to negotiate, quote pricing or describe your product, because an introduction made with the wrong commitments attached can cost more than it brings in.",
    majorTerms: [
      "Definition of a qualified referral and how it is registered",
      "Exclusion of accounts already in your pipeline",
      "Fee amount or rate and the base it is calculated on",
      "The trigger: introduction, signed agreement, first shipment or collected payment",
      "Payment timing and duration of any ongoing fee",
      "Duration of the referral's protection period",
      "No authority to negotiate, quote or bind the brand",
      "No claims about the product beyond approved materials",
      "Confidentiality of pricing and terms disclosed",
      "Term and termination",
    ],
    clausesToWatch: [
      {
        clause: "Definition of a qualified referral",
        why: "Without a written definition and an advance registration step, an email introduction to a contact you already had can become a fee claim.",
      },
      {
        clause: "Pre-existing relationship exclusion",
        why: "If accounts already in your pipeline are not excluded and listed, you can pay a finder's fee on business you sourced yourself.",
      },
      {
        clause: "When the fee is earned",
        why: "Paid on introduction, on signature or on collected revenue are very different risks, and the difference decides who carries a deal that signs and never ships.",
      },
      {
        clause: "Duration of ongoing fees",
        why: "A perpetual percentage on everything an account ever buys turns one introduction into a permanent margin reduction that follows you into a sale of the business.",
      },
      {
        clause: "Authority and representations",
        why: "A referrer who quotes a price, promises a term or describes what the product does creates expectations you have to meet or walk back.",
      },
      {
        clause: "Exclusivity over a channel or account",
        why: "A referral arrangement that quietly grants someone the exclusive right to introduce you into a channel can block you from working it directly.",
      },
    ],
    cpgIssues: [
      "Retail buyer introductions are the currency of this industry and are frequently offered by more than one person for the same account, so registration and a protection period are what prevent paying twice.",
      "A referral into a distributor or broker relationship can overlap with commissions already payable to a broker on the same account; check the broker agreement before agreeing a referral fee.",
      "Referral fees calculated on gross purchase orders ignore deductions, slotting and spoils in retail, so a fee that looked small can exceed the account's actual contribution.",
      "Introductions to co-packers, ingredient suppliers or 3PLs are sometimes paid for by the other side as well, so ask whether the referrer is being compensated by both parties.",
      "A referrer describing your supplement or functional beverage to a buyer should use only your approved materials, since claims made in the pitch follow the product onto the shelf.",
      "Chain accounts take a long time between introduction and first purchase order, so the protection period has to be long enough to be fair and short enough to end.",
    ],
    founderMistakes: [
      "Agreeing a referral fee verbally, then paying on an account the founder had been working for a year because nothing excluded it.",
      "Accepting a perpetual percentage on all future purchases by a referred account, and carrying it into an acquisition as a disclosed liability.",
      "Paying on introduction rather than on shipment, and funding introductions that never became orders.",
      "Letting a referrer quote pricing and terms to a buyer, and starting the relationship from a number the brand could not support.",
      "Never asking whether the referrer was also being paid by the distributor they were introducing.",
    ],
    negotiationPoints: [
      "Require referrals to be registered in writing in advance and confirmed by you before any introduction is made.",
      "Attach a list of accounts already in your pipeline that are excluded.",
      "Tie the fee to collected revenue or first shipment rather than to an introduction or a signature.",
      "Cap ongoing fees to a stated number of months or orders rather than accepting a perpetual rate.",
      "State expressly that the referrer has no authority to negotiate, quote or describe the product beyond approved materials.",
      "Require disclosure of any compensation the referrer receives from the other side.",
    ],
    redFlags: [
      "No written definition of a qualified referral.",
      "No exclusion for accounts already in your pipeline.",
      "A perpetual fee on all future business from the account.",
      "A fee earned on introduction alone.",
      "Any authority to negotiate or commit on your behalf.",
      "Exclusive rights over a channel granted inside a referral arrangement.",
    ],
    beforeYouSign: [
      "Your current pipeline list, written down and attached as the exclusion schedule.",
      "A worked example showing the fee on a realistic first year of orders from the account.",
      "Confirmation of whether the referrer is compensated by the other party as well.",
    ],
    related: [
      "commission-referral-agreement",
      "strategic-referral-partner-agreement",
      "affiliate-agreement",
      "cpg-broker-agreement",
      "commission-agreement",
    ],
    attorneyReview: "depends",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "brand-ambassador-agreement",
    name: "Brand Ambassador Agreement",
    category: "affiliate",
    subcategory: "Ambassadors",
    aliases: [
      "Ambassador Program Agreement",
      "Brand Rep Agreement",
      "Community Ambassador Agreement",
      "Athlete Ambassador Agreement",
    ],
    stages: ["launch", "growth"],
    importance: "common",
    entityTypes: ["any"],
    parties: "Brand + Ambassador",
    whenYouNeedIt:
      "When you build a roster of people who represent the brand over time — posting, sampling at events, running a code, showing up in your content — rather than doing one paid post and moving on.",
    whatItDoes:
      "Sets what the ambassador does, what they receive in product, commission or fee, what they may say and show, who owns the content they create, and how the relationship ends including what happens to their code and their posts.",
    whyItMatters:
      "Ambassador programs blur three lines at once, and each one costs money when it is left blurred. The first is content: if the agreement does not grant advertising rights, you have a nice post rather than an asset you can run as a paid ad. The second is what they say — an ambassador describing your functional beverage or supplement as doing something for health or weight is making a claim the brand carries, whatever the caption says. The third is how the relationship actually operates: ambassadors who are scheduled, supervised and required to work set shifts start to look like something other than an independent arrangement, and that question varies by jurisdiction and belongs with counsel before the roster grows.",
    majorTerms: [
      "Term and renewal",
      "Deliverables: posts, events, sampling shifts, appearances",
      "Compensation: product allowance, flat fee, commission code, or a blend",
      "Unique discount code and commission rules",
      "Content ownership, licence scope and paid advertising rights",
      "Whitelisting or permission to run ads from their handle",
      "Approved claims and messaging boundaries",
      "Disclosure of the paid relationship in every post",
      "Exclusivity within the category and conflicting brands",
      "Name, image and likeness rights and how long they last",
      "Conduct standards and termination rights",
      "Post-termination content, code deactivation and takedown",
    ],
    clausesToWatch: [
      {
        clause: "Content licence scope",
        why: "A licence covering organic posting only leaves you unable to run the content as a paid ad, which is usually the reason you wanted it.",
      },
      {
        clause: "Likeness rights after the term",
        why: "If the right to use their image ends with the agreement, your existing ads, packaging and retailer materials have to come down with it.",
      },
      {
        clause: "Approved claims",
        why: "Ambassadors talk about how the product makes them feel, which is exactly where claims about a supplement or functional beverage drift beyond what the brand can support.",
      },
      {
        clause: "Disclosure of the relationship",
        why: "Undisclosed paid promotion is a consumer-facing exposure the brand carries, and the requirement should be explicit rather than assumed.",
      },
      {
        clause: "Scheduling and supervision",
        why: "Required shifts, set hours and close direction change the character of the engagement, which is a classification question for counsel where the ambassador works.",
      },
      {
        clause: "Category exclusivity",
        why: "Without a defined category, an ambassador can represent a directly competing product the following month while your content is still running.",
      },
      {
        clause: "Termination and code deactivation",
        why: "Codes that stay live and posts that stay up after a bad exit keep spending your money and carrying your brand.",
      },
    ],
    cpgIssues: [
      "Ambassadors who receive free product need the value and the volume defined, or a product allowance quietly becomes a meaningful cost across a large roster.",
      "Ambassadors sampling at events handle food and beverage in public, which brings hygiene, allergen and insurance expectations that belong in the agreement or in the event paperwork.",
      "Claims about energy, focus, recovery, gut health or weight are the natural language of ambassador content for functional products, and what may be said varies and is fact-specific — supply an approved list and have counsel set it.",
      "Ambassadors with a discount code are effectively affiliates, so code stacking, attribution and clawback on refunds all apply and should be written the same way.",
      "Athletes, trainers and practitioners carry an implied authority that raises the stakes on anything they say about a supplement, and often carry obligations to other sponsors that need disclosing.",
      "Ambassador content frequently shows old packaging, discontinued flavours or superseded label copy, so require the current asset kit and a takedown route for outdated material.",
      "Ambassadors sometimes resell gifted product or list it on marketplaces; prohibit resale explicitly and state that product is supplied for personal use and sampling.",
    ],
    founderMistakes: [
      "Building a roster on free product and a code, with no written agreement, and having no right to use any of the content in paid media.",
      "Discovering an ambassador had described the product as treating a health condition, with no approved-claims list to point to and no fast takedown right.",
      "Scheduling ambassadors like staff across dozens of shifts without ever raising the arrangement with counsel.",
      "Losing the right to use an ambassador's face in a retailer display when the term expired, after the display had already shipped.",
      "Letting a departing ambassador's code stay live for months and paying commission on it.",
      "Signing an ambassador who joined a competing brand six weeks later, with no category exclusivity anywhere in the document.",
    ],
    negotiationPoints: [
      "Take a content licence that is perpetual for content created during the term, and expressly covers paid advertising and retailer use.",
      "Separate likeness rights from the engagement term, so existing creative can keep running after the relationship ends.",
      "Attach an approved-claims list and prohibit anything outside it, with immediate takedown on request.",
      "Require clear disclosure of the paid relationship in every piece of content, in the form counsel advises.",
      "Define category exclusivity narrowly enough to be fair and clearly enough to be enforceable, and require disclosure of other brand relationships.",
      "State the product allowance in units per period, and prohibit resale of gifted product.",
      "Provide for immediate termination, code deactivation and content takedown, and say who pays for what after exit.",
      "Have counsel review the scheduling and supervision pattern before scaling the roster.",
    ],
    redFlags: [
      "No written content licence, or one limited to organic posting.",
      "Likeness rights ending with the term while your ads are still running.",
      "No approved-claims list for a supplement or functional beverage.",
      "No disclosure requirement.",
      "Set shifts and close supervision with no classification review.",
      "No category exclusivity and no obligation to disclose competing relationships.",
      "No right to require takedown or deactivate a code on termination.",
    ],
    beforeYouSign: [
      "A written approved-claims list, reviewed by counsel, and the current asset kit.",
      "A defined deliverables schedule and product allowance in units.",
      "Confirmation of their existing brand relationships and any conflicting obligations.",
      "Counsel input on the engagement pattern if ambassadors will be scheduled or supervised.",
    ],
    related: [
      "influencer-affiliate-agreement",
      "affiliate-agreement",
      "ambassador-agreement",
      "event-brand-ambassador-agreement",
      "nil-release",
      "independent-contractor-agreement",
    ],
    attorneyReview: "yes",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "influencer-affiliate-agreement",
    name: "Influencer Affiliate Agreement",
    category: "affiliate",
    subcategory: "Ambassadors",
    aliases: [
      "Creator Affiliate Agreement",
      "Influencer Commission Agreement",
      "Performance Creator Agreement",
      "Hybrid Influencer Agreement",
    ],
    stages: ["launch", "growth"],
    importance: "common",
    entityTypes: ["any"],
    parties: "Brand + Influencer / Creator (on commission)",
    whenYouNeedIt:
      "When a creator is paid on performance rather than on a flat fee — a commission code, an affiliate link, a revenue share, or a smaller fee plus a percentage of what their audience buys.",
    whatItDoes:
      "Combines a content agreement with an affiliate arrangement: it sets the deliverables and usage rights on one side, and the tracking, commission, attribution and clawback rules on the other, so both halves are governed by the same document.",
    whyItMatters:
      "The hybrid structure creates a conflict the agreement has to manage: the creator is paid to drive conversions, and the fastest way to drive conversions for a supplement or functional beverage is to say something about what it does. Those claims land on your brand, and what may be said about products of this kind is fact-specific and varies, so it is a counsel question rather than a creative one. Commercially, the other half of the problem is that a commission code on a low-basket product stacks with your own promotions and can turn a strong-looking campaign into a loss per order, while a long attribution window quietly pays for traffic you already had.",
    majorTerms: [
      "Deliverables: number, format, platform and posting schedule",
      "Flat fee, commission rate and how they interact",
      "Unique code or link, attribution model and tracking window",
      "Commission base and treatment of discounts and shipping",
      "Payment timing, holding period and clawback on refunds",
      "Content ownership and licence, including paid advertising rights",
      "Whitelisting or ad-account permissions and their duration",
      "Approved claims and prohibited messaging",
      "Disclosure of the paid relationship",
      "Competing brand restrictions and disclosure obligations",
      "Content approval, revisions and takedown rights",
      "Term, renewal and post-termination code handling",
    ],
    clausesToWatch: [
      {
        clause: "Usage rights versus the post",
        why: "Paying for a post buys the post; running it as an ad, on your site, in a retailer deck or on packaging each needs to be granted expressly.",
      },
      {
        clause: "Whitelisting permissions",
        why: "Running ads from a creator's handle requires a separate grant with a stated duration, and losing access mid-campaign strands the spend behind it.",
      },
      {
        clause: "Approved claims list",
        why: "Performance pay pushes creators toward stronger statements, and a claim about health, weight or a condition made to sell your product becomes your exposure.",
      },
      {
        clause: "Attribution window and code stacking",
        why: "A long window plus a stackable code means you pay commission on repeat customers who used the code as a discount rather than discovering you through the creator.",
      },
      {
        clause: "Clawback on refunds and chargebacks",
        why: "Creator-driven traffic on a consumable can carry a higher refund rate, and without a holding period you pay commission on orders you refunded.",
      },
      {
        clause: "Competing brands",
        why: "A creator promoting three products in your category in the same month dilutes the endorsement and can put your content beside a direct competitor's.",
      },
      {
        clause: "Takedown and correction rights",
        why: "Without a contractual right to require an edit or removal, a non-compliant or off-claim post stays up while you negotiate.",
      },
    ],
    cpgIssues: [
      "Supplements and functional beverages attract exactly the claims that cause trouble — energy, focus, sleep, gut health, weight — and a creator paid on conversion has every incentive to make them; the brand carries that exposure, so counsel should set the approved list before the brief goes out.",
      "Before-and-after content, transformation posts and testimonial framing raise the stakes further and should be addressed explicitly rather than left to the creator's judgement.",
      "Codes given to creators behave like affiliate codes: they get posted to deal sites, stacked with promotions and used by existing customers, so uniqueness, stacking rules and clawback all belong here.",
      "Low average order value plus parcel cost means a commission that is fine on a large basket can be fatal on a single unit; model it before agreeing the rate.",
      "Creators frequently show product that has since changed — old label, discontinued flavour, superseded formulation — so require the current asset kit and a route to update or remove outdated content.",
      "Creators who resell gifted product or list it on marketplaces create unauthorised listings; prohibit resale and marketplace activity expressly.",
      "Subscription offers promoted by creators need the commission treatment stated for renewals, or you can be paying a percentage of a recurring order indefinitely.",
    ],
    founderMistakes: [
      "Paying for a set of posts that performed well and then discovering the agreement gave no right to run any of it as paid media.",
      "Setting a commission rate on gross order value including shipping, and losing money on every single-unit order the campaign produced.",
      "Never supplying an approved-claims list, then finding the highest-performing video described the product as a treatment.",
      "Agreeing whitelisting verbally, losing access when the creator changed agencies, and stranding an active ad campaign.",
      "Allowing the code to be stacked with a site-wide sale, so existing customers used it and the creator was paid on them.",
      "Signing a creator who posted for a direct competitor two weeks later, with no exclusivity and no disclosure obligation.",
    ],
    negotiationPoints: [
      "Grant deliverables and usage rights separately, and take paid advertising, website, retailer and organic rights expressly with a stated duration.",
      "Agree whitelisting in writing with a duration that outlasts the campaign, and a process if access is lost.",
      "Attach the approved-claims list and prohibit health, weight and condition claims outside it, with a same-day takedown obligation.",
      "Issue a unique, non-stackable code, set a short attribution window, and exclude existing subscribers where you can.",
      "Hold commission for a period matched to your refund window, with automatic clawback.",
      "Require clear disclosure of the paid relationship in the form counsel advises for each platform.",
      "Set a category exclusivity period and require disclosure of competing engagements.",
      "Keep an unconditional right to require correction or removal of any content.",
    ],
    redFlags: [
      "No approved-claims list for a supplement or functional product.",
      "Usage rights limited to the original post.",
      "Commission on gross order value including shipping, with no clawback.",
      "A stackable code with a long attribution window.",
      "No disclosure requirement.",
      "No takedown or correction right.",
      "Ongoing commission on subscription renewals with no cap.",
    ],
    beforeYouSign: [
      "A counsel-approved claims list and a written creative brief that reflects it.",
      "A fully loaded margin model for the order sizes this creator will actually produce.",
      "The usage rights you need for paid media, your site and retailer materials, listed explicitly.",
      "The tracking setup tested, including refunds and chargebacks flowing back through it.",
    ],
    related: [
      "affiliate-agreement",
      "brand-ambassador-agreement",
      "influencer-agreement",
      "ugc-creator-agreement",
      "influencer-disclosure",
      "content-creator-agreement",
    ],
    attorneyReview: "strongly-recommended",
    attorneyNote:
      "Performance pay pushes creators toward claims about what a supplement or functional product does, and those claims attach to the brand alongside consumer-facing disclosure obligations that vary by jurisdiction and platform.",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "commission-referral-agreement",
    name: "Commission Referral Agreement",
    category: "affiliate",
    subcategory: "Referral",
    aliases: [
      "Ongoing Referral Commission Agreement",
      "Revenue Share Referral Agreement",
      "Introducer Commission Agreement",
    ],
    stages: ["launch", "distribution", "growth"],
    importance: "situational",
    entityTypes: ["any"],
    parties: "Brand + Referring Party (paid on ongoing revenue)",
    whenYouNeedIt:
      "When a referral is paid as a percentage of what the referred customer buys over time rather than as a one-off fee — common with wholesale accounts, subscription customers and foodservice introductions.",
    whatItDoes:
      "Sets an ongoing commission on revenue from a referred account: how the account is registered, what revenue counts, for how long the commission runs, what reduces it, and what happens when the relationship ends.",
    whyItMatters:
      "An ongoing percentage sounds like a fair way to share success and behaves like a permanent margin reduction. The person who made the introduction is paid on every future order whether or not they are still involved, and if the term is open-ended the obligation follows the account for years and shows up as a disclosed liability when you sell the business. The other trap is the base: a percentage of gross purchase orders in a channel that deducts promotional funding, spoils and slotting can exceed what the account actually contributes.",
    majorTerms: [
      "Account registration and confirmation process",
      "Excluded and pre-existing accounts",
      "Commission rate and any step-down over time",
      "Revenue base: gross invoice, net of deductions, or collected revenue",
      "Duration of the commission and any total cap",
      "When commission is earned and when it is paid",
      "Treatment of returns, deductions, refunds and unpaid invoices",
      "What happens if the referrer stops being involved",
      "Assignment on a sale of the brand",
      "Confidentiality of pricing and account terms",
      "Termination and survival of accrued rights",
    ],
    clausesToWatch: [
      {
        clause: "Duration of the commission",
        why: "An uncapped, perpetual percentage on an account converts a single introduction into an obligation that outlives everyone involved and reduces what a buyer will pay for the business.",
      },
      {
        clause: "The revenue base",
        why: "Gross purchase order value ignores deductions, allowances and returns, so on a retail account the commission can outrun the margin.",
      },
      {
        clause: "Continuing involvement",
        why: "If the commission does not depend on the referrer doing anything after the introduction, you are paying a servicing rate for no service.",
      },
      {
        clause: "Assignment on a sale",
        why: "An obligation that binds a buyer of your brand, or that triggers a payment on a change of control, comes straight out of your purchase price.",
      },
      {
        clause: "Clawback for returns and unpaid invoices",
        why: "Without a mechanism, commission paid on an order that is later credited or never collected simply stays paid.",
      },
      {
        clause: "Account registration and overlap",
        why: "Two people can claim the same account; a registration process with a confirmation step is what prevents paying both.",
      },
    ],
    cpgIssues: [
      "Wholesale and distributor accounts settle net of deductions, slotting, spoils and promotional funding, so commission should sit on collected revenue rather than on the purchase order face value.",
      "A referred distributor may already be paying a broker on the same account; check the broker and distributor agreements before layering another percentage on.",
      "Retail accounts take months to move from introduction to first shipment, so the earning trigger should be shipment or collection, not the introduction.",
      "Subscription and direct-to-consumer referrals need a stated number of cycles, since an unlimited percentage on a recurring consumable compounds quietly.",
      "Foodservice and institutional accounts often reorder for years with no further involvement from the introducer, which is exactly the case for a step-down or a cap.",
      "Volume growth in an account usually comes from your own trade spend and service, so a rate that never declines pays the referrer for work someone else did.",
    ],
    founderMistakes: [
      "Agreeing a perpetual percentage on a chain account, and disclosing it as an ongoing liability during acquisition diligence.",
      "Calculating commission on gross purchase orders in a deduction-heavy retailer, and paying more than the account contributed.",
      "Paying on the same account to two different introducers because there was no registration step.",
      "Continuing to pay after the referrer stopped having any involvement, because nothing conditioned the commission on activity.",
      "Failing to include a clawback, and paying commission on a large opening order that came back as unsaleable.",
    ],
    negotiationPoints: [
      "Cap the commission by time, by total amount, or with a step-down that declines each year.",
      "Set the base as collected revenue net of deductions, allowances, returns and freight.",
      "Require written account registration and your written confirmation before any introduction counts.",
      "Condition continuing commission on continuing involvement, defined in the agreement.",
      "Include clawback for credits, returns and uncollected invoices.",
      "Address a sale of the brand expressly: no consent right, no change-of-control payment, and a clean way to buy out the obligation.",
    ],
    redFlags: [
      "A perpetual, uncapped percentage with no step-down.",
      "Commission on gross invoice value with no adjustment for deductions or returns.",
      "No registration or confirmation process for accounts.",
      "No clawback mechanism.",
      "A payment or consent right triggered by a sale of the brand.",
      "Commission that continues regardless of whether the referrer does anything.",
    ],
    beforeYouSign: [
      "A model of the commission over five years of realistic volume from the account.",
      "The existing broker, distributor and commission arrangements covering the same account.",
      "A written buy-out mechanism you would be happy to use later.",
    ],
    related: [
      "referral-agreement",
      "strategic-referral-partner-agreement",
      "affiliate-agreement",
      "commission-agreement",
      "cpg-broker-agreement",
    ],
    attorneyReview: "depends",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },

  {
    id: "strategic-referral-partner-agreement",
    name: "Strategic Referral Partner Agreement",
    category: "affiliate",
    subcategory: "Partnerships",
    aliases: [
      "Channel Partner Referral Agreement",
      "Strategic Partnership Referral Agreement",
      "Reciprocal Referral Agreement",
      "Partner Program Agreement",
    ],
    stages: ["distribution", "growth"],
    importance: "situational",
    entityTypes: ["any"],
    parties: "Brand + Strategic Partner (business-to-business referrals)",
    whenYouNeedIt:
      "When another business sends you customers as a matter of course — a complementary brand, an agency, a broker, a co-packer, a retailer-services firm — and you want a standing arrangement rather than a fee negotiated per introduction.",
    whatItDoes:
      "Creates a repeatable referral relationship between two businesses: how leads are passed and registered, what each side is paid, whether it runs in both directions, what may be said about the other's products, and how customer data is handled.",
    whyItMatters:
      "Partner referrals are the highest-quality leads a young CPG brand gets and the easiest place to create obligations you did not intend. A partner introducing you to retailers and distributors is standing next to your brand in front of buyers, so what they say about your product, your certifications and your capacity becomes your problem. Reciprocal arrangements add a second layer: agreeing to send your customers to a partner puts your relationships behind someone else's service quality, and unwinding that when the partnership sours is harder than starting it.",
    majorTerms: [
      "Scope: which customers, channels and territories the arrangement covers",
      "Lead registration, confirmation and protection period",
      "Fee structure, and whether it runs one way or both",
      "What each party may say about the other and approved materials",
      "Use of each other's trademarks, logos and case studies",
      "Customer data sharing, consent and permitted use",
      "Exclusivity or non-exclusivity of the partnership",
      "Conflicts with competing brands the partner also serves",
      "Confidentiality of pricing, terms and customer information",
      "Term, termination and treatment of leads in flight",
      "No authority to bind or negotiate on the other's behalf",
    ],
    clausesToWatch: [
      {
        clause: "Statements about the other party's products",
        why: "A partner pitching your supplement or functional beverage to a buyer can make claims you would never make, and the brand carries the consequence.",
      },
      {
        clause: "Customer data sharing",
        why: "Passing contact data between businesses raises consent and permitted-use questions that vary by jurisdiction and belong with counsel before the first list is shared.",
      },
      {
        clause: "Exclusivity",
        why: "An exclusive partner arrangement in a channel can lock you out of working it directly or with anyone better, for as long as the term runs.",
      },
      {
        clause: "Trademark and case-study use",
        why: "Without approval rights, your brand appears in the partner's marketing next to claims, clients and positioning you did not choose.",
      },
      {
        clause: "Reciprocity obligations",
        why: "A commitment to refer your customers to a partner puts your relationships behind their service quality, and there is rarely a stated remedy when it disappoints.",
      },
      {
        clause: "Leads in flight at termination",
        why: "Without a rule, ending the partnership starts an argument about every open opportunity either side introduced.",
      },
      {
        clause: "Conflicts with competing brands",
        why: "Partners in this industry usually serve several brands, and undisclosed overlap in your category undermines the value of the arrangement.",
      },
    ],
    cpgIssues: [
      "Brokers, agencies and co-packers all refer brands to each other constantly, and the same account can be claimed under a referral arrangement and a broker commission at once — reconcile them before signing.",
      "A partner representing your product to a retail buyer should use only your approved materials and claims, since a buyer's expectation set wrongly in that meeting follows you through the whole listing.",
      "Partners often have views on your certifications, capacity and lead times; require them to describe your operational capability only from your written materials.",
      "Reciprocal referrals into a co-packer, 3PL or ingredient supplier can put you in the middle when the partner underperforms for a customer you sent.",
      "Customer lists in this industry are small and overlapping, so lead registration and a protection period matter more than the fee itself.",
      "A complementary brand partnership can drift into co-branding — shared packaging, joint promotions, bundled products — which needs its own agreement rather than an expanded referral clause.",
    ],
    founderMistakes: [
      "Agreeing an exclusive channel partnership that blocked the brand from working the channel directly for the whole term.",
      "Letting a partner pitch the product with their own materials, and inheriting a buyer expectation on capacity the brand could not meet.",
      "Sharing customer contact data with a partner with no written terms on consent or permitted use.",
      "Layering a partner referral fee on an account that was already paying a broker commission, and discovering it at the first invoice.",
      "Having no rule for leads in flight, and ending a partnership in a dispute over every open opportunity.",
      "Letting a referral partnership turn into a co-branded product with nothing but the referral agreement behind it.",
    ],
    negotiationPoints: [
      "Keep the arrangement non-exclusive unless exclusivity is paid for with a committed volume of qualified referrals.",
      "Require both parties to use only the other's approved materials and approved claims.",
      "Set a lead registration process with written confirmation and a stated protection period.",
      "Agree customer data terms with counsel before any list is shared, including permitted use and deletion on termination.",
      "Reserve trademark and case-study approval rights.",
      "Reconcile the fee against any broker or commission arrangements already covering the same accounts.",
      "State how leads in flight are treated on termination, and cap any tail.",
      "Require disclosure of the partner's other clients in your category.",
    ],
    redFlags: [
      "Exclusivity over a channel with no committed referral volume.",
      "No restriction on what the partner may say about your product.",
      "Customer data sharing with no written terms.",
      "An open-ended tail on leads after termination.",
      "No trademark or case-study approval right.",
      "A reciprocal obligation to refer with no quality standard or exit.",
      "Silence on the partner's relationships with competing brands.",
    ],
    beforeYouSign: [
      "A written list of the accounts and channels each side is bringing, and what is excluded.",
      "The partner's disclosed relationships with brands in your category.",
      "Counsel input on customer data sharing before any list changes hands.",
    ],
    related: [
      "referral-agreement",
      "commission-referral-agreement",
      "affiliate-agreement",
      "cross-marketing-agreement",
      "promotional-partnership-agreement",
      "cpg-broker-agreement",
    ],
    attorneyReview: "yes",
    templateId: null,
    templateVersion: null,
    lastReviewed: "2026-08-25",
  },
];

export default AFFILIATE_DOCS;
